import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy, signal, computed, input } from '@angular/core';
import { FilterService } from '../services/filter.service';
import { of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { FilterInputDirective } from './filter-input.directive';
import { FilterName } from '../services/filter-name';
import { IconComponent } from '@cumlaude/shared-components-icons';

@Component({
	selector: 'app-filter-container',
	templateUrl: './filter-container.component.html',
	styleUrls: ['./filter-container.component.scss'],
	imports: [FilterInputDirective, AsyncPipe, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterContainerComponent<T> implements OnInit, OnDestroy {
	private readonly filterService = inject(FilterService);

	private readonly SHOW_SEARCH_AFTER = 10;

	readonly filterName = input.required<FilterName>();

	label = computed(() => this.filterService.configs[this.filterName()]!.label);

	/**
	 * Geselecteerde waarde zoals die getoond moet worden bij dichtgeklapte container.
	 */
	valueString!: string | undefined;

	/**
	 * Of het filter te wissen (niet-leeg en optional) is.
	 */
	canErase$ = of(false);

	/**
	 * Of de gebruiker zelf het searchIcon heeft aangeklikt in dit component.
	 */
	searchWanted = signal(false);

	searchActivated = computed(() => (this.searchWanted() || this.filterService.isSingleSelect(this.filterName())) && this.enoughValuesForSearch());

	/**
	 * Of er zo veel waarden zijn dat er gezocht moet kunnen worden.
	 */
	enoughValuesForSearch = signal(false);

	/**
	 * Input van de searchbox uit het filtercomponent, moet onthouden worden wanneer de searchbox
	 * verborgen is d.m.v. het searchIcon of door de hele filtercontainer in te klappen. (De opties
	 * moeten in die gevallen niet meer gefilterd worden door de searchInput).
	 */
	searchInput = signal('');

	opened = signal(false);

	showSearchIcon = computed(() => this.enoughValuesForSearch() && this.filterService.isMultiSelect(this.filterName()) && this.opened());

	protected subscriptions: Subscription[] = [];

	ngOnInit() {
		const preactivated = this.filterService.isActive(this.filterName());
		if (preactivated) this.onFilterActivated();
	}

	ngOnDestroy() {
		this.onFilterDeactivated();
	}

	onFilterActivated() {
		const filterName = this.filterName();
		const { valueString } = this.filterService.configs[filterName]!;
		const val$ = this.filterService.getFilterStateAsInput(filterName);
		this.canErase$ = val$.pipe(map((val) => val !== undefined && this.filterService.isOptional(filterName)));
		this.subscriptions.push(
			val$.subscribe((val) => {
				this.valueString = val === undefined ? undefined : valueString(val);
			}),
			this.filterService.options[filterName]!.subscribe((filterValues) => {
				this.enoughValuesForSearch.set(filterValues.activeValues.length + filterValues.inactiveValues.length > this.SHOW_SEARCH_AFTER);
			}),
			this.filterService.filterPanelOpened$.subscribe((open) => {
				if (!open) this.resetSearchFilter();
			})
		);
	}

	onFilterDeactivated() {
		for (const sub of this.subscriptions) sub.unsubscribe();
	}

	open(open: boolean) {
		this.opened.set(open);
		const filterName = this.filterName();
		if (open) {
			if (!this.filterService.isActive(filterName)) {
				this.filterService.activate([filterName], true, { [filterName]: undefined });
				this.onFilterActivated();
			}
		} else {
			this.filterService.deactivateIfNeeded([filterName]);
			this.onFilterDeactivated();
		}
	}

	toggleSearch($event: Event) {
		$event.stopPropagation();

		this.searchWanted.update((value) => !value);
	}

	erase($event: Event) {
		$event.stopPropagation();
		this.resetSearchFilter();
		const filterName = this.filterName();
		this.filterService.setFilterInput(filterName, undefined).then(() => this.filterService.deactivateIfNeeded([filterName]));
	}

	resetSearchFilter() {
		this.searchInput.set('');
	}
}
